import { Vector3 } from "three";
import { customElement, property } from "lit/decorators.js";
import { tryRaycastOnPlaneFromScreen } from "../../physics/PhysicsUtil";
import { createDisposable } from "../events/messaging/index";
import { ViewportCapabilityElement } from "../viewport/ViewportCapabilityElement";
import type { ViewportContext } from "../viewport/ViewportContext";

export const SUGAR_FILE_DROP_EVENT = "sugar-file-drop";
export const SUGAR_FILE_DRAG_OVER_EVENT = "sugar-file-drag-over";
export const SUGAR_FILE_DRAG_LEAVE_EVENT = "sugar-file-drag-leave";

export interface SugarFileDropDetail {
  /** Kabul filtresinden geçen dosyalar. */
  readonly files: readonly File[];
  /**
   * Drop noktasının dünya koordinatı (zemin düzlemine ışın atılarak bulunur).
   * Kamera yoksa veya ışın düzleme paralelse `undefined`.
   */
  readonly point?: Vector3;
  /** Ham DOM olayı — ek bilgi gerekirse. */
  readonly originalEvent: DragEvent;
}

export interface SugarFileDragOverDetail {
  readonly point?: Vector3;
  readonly originalEvent: DragEvent;
}

const GROUND_PLANE = {
  position: new Vector3(0, 0, 0),
  normal: new Vector3(0, 1, 0),
};

/**
 * `<sugar-file-drop>` — viewport'a dışarıdan (işletim sisteminden veya başka
 * bir uygulamadan) dosya bırakmayı standartlaştıran yetenek elementi.
 *
 * Neden ayrı bir element: dosya drop'unun her üründe tekrarlanan üç can sıkıcı
 * tarafı var ve üçü de burada bir kez çözülüyor:
 *
 *  1. `dragover`'da `preventDefault()` çağrılmazsa `drop` hiç ateşlenmez ve
 *     tarayıcı dosyayı açmak için sayfadan ayrılır.
 *  2. `dragenter`/`dragleave` çocuk elementlerde de tetiklendiği için sayaç
 *     tutulmadan "sürükleme bitti mi" bilinemez.
 *  3. Drop noktası ekran koordinatıdır; sahnede işe yaraması için dünya
 *     koordinatına çevrilmesi gerekir.
 *
 * Kullanım — viewport'un içine koy:
 *
 * ```html
 * <sugar-viewport>
 *   <sugar-file-drop accept=".glb,.gltf"></sugar-file-drop>
 * </sugar-viewport>
 * ```
 *
 * Ürün controller'ı olayı dinler; ne yapılacağına o karar verir:
 *
 * ```ts
 * viewport.addEventListener(SUGAR_FILE_DROP_EVENT, (e) => {
 *   const { files, point } = (e as CustomEvent<SugarFileDropDetail>).detail;
 *   void this.loadDroppedModel(files[0], point);
 * });
 * ```
 *
 * Olaylar `bubbles: true, composed: true` ile yayılır; ürün elementi de
 * dinleyebilir.
 */
@customElement("sugar-file-drop")
export class SugarFileDropElement extends ViewportCapabilityElement {
  /**
   * Kabul edilen uzantı ve/veya MIME listesi (virgülle ayrılmış).
   * Boş bırakılırsa her dosya kabul edilir.
   * Örnek: `accept=".glb,.gltf,model/gltf-binary"`
   */
  @property({ type: String })
  accept = "";

  /** Birden fazla dosya bırakılabilir mi. */
  @property({ type: Boolean })
  multiple = false;

  /** Sürükleme sırasında host elemente eklenen class. */
  @property({ type: String, attribute: "active-class" })
  activeClass = "sugar-file-drop-active";

  /** İç içe dragenter/dragleave sayacı. */
  private depth = 0;

  protected override onViewportConnected(context: ViewportContext) {
    const host = context.host;
    const onDragEnter = (event: DragEvent) => this.handleDragEnter(event, host);
    const onDragOver = (event: DragEvent) => this.handleDragOver(event, context);
    const onDragLeave = (event: DragEvent) => this.handleDragLeave(event, host);
    const onDrop = (event: DragEvent) => this.handleDrop(event, context, host);

    host.addEventListener("dragenter", onDragEnter);
    host.addEventListener("dragover", onDragOver);
    host.addEventListener("dragleave", onDragLeave);
    host.addEventListener("drop", onDrop);

    return createDisposable(() => {
      host.removeEventListener("dragenter", onDragEnter);
      host.removeEventListener("dragover", onDragOver);
      host.removeEventListener("dragleave", onDragLeave);
      host.removeEventListener("drop", onDrop);
      host.classList.remove(this.activeClass);
      this.depth = 0;
    });
  }

  /** Sürüklenen şey dosya mı (katalogdan gelen iç sürükleme değil). */
  private carriesFiles(event: DragEvent): boolean {
    const types = event.dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes("Files");
  }

  private accepts(file: File): boolean {
    const filter = this.accept.trim();
    if (!filter) return true;
    const name = file.name.toLowerCase();
    const type = (file.type || "").toLowerCase();
    return filter
      .split(",")
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean)
      .some((rule) =>
        rule.startsWith(".") ? name.endsWith(rule) : type === rule,
      );
  }

  private worldPoint(
    event: DragEvent,
    context: ViewportContext,
  ): Vector3 | undefined {
    const camera = context.engine.activeCamera?.instance;
    const canvas = context.engine.canvas;
    if (!camera || !canvas) return undefined;
    return tryRaycastOnPlaneFromScreen(event, GROUND_PLANE, camera, canvas);
  }

  private handleDragEnter(event: DragEvent, host: HTMLElement): void {
    if (!this.carriesFiles(event)) return;
    event.preventDefault();
    this.depth += 1;
    if (this.depth === 1) host.classList.add(this.activeClass);
  }

  private handleDragOver(event: DragEvent, context: ViewportContext): void {
    if (!this.carriesFiles(event)) return;
    // Bu olmadan `drop` hiç ateşlenmez.
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";

    this.emit<SugarFileDragOverDetail>(SUGAR_FILE_DRAG_OVER_EVENT, {
      point: this.worldPoint(event, context),
      originalEvent: event,
    });
  }

  private handleDragLeave(event: DragEvent, host: HTMLElement): void {
    if (!this.carriesFiles(event)) return;
    this.depth = Math.max(0, this.depth - 1);
    if (this.depth === 0) {
      host.classList.remove(this.activeClass);
      this.emit(SUGAR_FILE_DRAG_LEAVE_EVENT, undefined);
    }
  }

  private handleDrop(
    event: DragEvent,
    context: ViewportContext,
    host: HTMLElement,
  ): void {
    if (!this.carriesFiles(event)) return;
    event.preventDefault();
    this.depth = 0;
    host.classList.remove(this.activeClass);

    const all = Array.from(event.dataTransfer?.files ?? []);
    const accepted = all.filter((file) => this.accepts(file));
    if (accepted.length === 0) return;

    this.emit<SugarFileDropDetail>(SUGAR_FILE_DROP_EVENT, {
      files: this.multiple ? accepted : accepted.slice(0, 1),
      point: this.worldPoint(event, context),
      originalEvent: event,
    });
  }

  private emit<T>(type: string, detail: T): void {
    this.dispatchEvent(
      new CustomEvent<T>(type, { detail, bubbles: true, composed: true }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sugar-file-drop": SugarFileDropElement;
  }
}
